import { asDate, formatDuration } from './formatters';
import { buildTimelineState } from './timeline';

export const LINK_STAGE_SLA_SECONDS = {
  approval: { warning: 120, critical: 300, label: 'Approval' },
  store: { warning: 300, critical: 600, label: 'Store wait' },
  packing: { warning: 420, critical: 900, label: 'Packing' },
  driver: { warning: 240, critical: 420, label: 'Driver wait' },
  delivery: { warning: 1800, critical: 2700, label: 'Delivery' },
};

const DELAY_LABELS = {
  approval: 'Approval delay',
  store: 'Store handoff delay',
  packing: 'Packing delay',
  driver: 'Driver delay',
  delivery: 'Delivery delay',
  none: 'On track',
};

function severityFor(seconds = 0, sla = {}) {
  if (!seconds) return 'healthy';
  if (seconds >= Number(sla.critical || 0)) return 'critical';
  if (seconds >= Number(sla.warning || 0)) return 'warning';
  return 'healthy';
}

function secondsSince(value) {
  const dt = asDate(value);
  if (!dt) return 0;
  return Math.max(0, Math.round((Date.now() - dt.getTime()) / 1000));
}

function currentStageSeconds(order = {}, timeline = {}) {
  const stage = order.stage || timeline.currentStage || 'new';

  if (order.issueFlag || stage === 'issue' || timeline.issueAt) {
    return { source: 'approval', seconds: timeline.approvalDelaySeconds || secondsSince(timeline.issueAt || order.updatedAt || order.createdAt) };
  }

  if (stage === 'awaiting_store') {
    if (timeline.packingAt && !timeline.readyAt) {
      return { source: 'packing', seconds: secondsSince(timeline.packingAt) };
    }
    return { source: 'store', seconds: timeline.storeWaitSeconds || secondsSince(timeline.routedAt || order.createdAt) };
  }

  if (stage === 'ready_for_driver') {
    return { source: 'driver', seconds: timeline.readyForDriverSeconds || secondsSince(timeline.readyAt || order.updatedAt || order.createdAt) };
  }

  if (stage === 'out_for_delivery') {
    return { source: 'delivery', seconds: timeline.deliveryRunSeconds || secondsSince(timeline.outAt || timeline.collectedAt || order.updatedAt || order.createdAt) };
  }

  return { source: 'none', seconds: 0 };
}

export function getDelayAttribution(order = {}) {
  const timeline = buildTimelineState(order.events || order.timeline || [], order.raw || order);
  const active = currentStageSeconds(order, timeline);
  const sla = LINK_STAGE_SLA_SECONDS[active.source] || {};
  const severity = severityFor(active.seconds, sla);
  const label = DELAY_LABELS[active.source] || 'Operational delay';

  return {
    source: active.source,
    label,
    seconds: active.seconds,
    durationLabel: active.seconds ? formatDuration(active.seconds) : '',
    severity,
    isDelayed: severity === 'warning' || severity === 'critical',
    sla,
    timeline,
  };
}

export function getDelayClass(delay = {}) {
  if (delay.severity === 'critical') return 'link-delay-critical';
  if (delay.severity === 'warning') return 'link-delay-warning';
  if (delay.severity === 'healthy') return 'link-delay-healthy';
  return '';
}

export function summarizeDelayAttribution(orders = []) {
  const live = orders.filter((order) => !['delivered', 'cancelled'].includes(order.stage));
  const totals = { approval: 0, store: 0, packing: 0, driver: 0, delivery: 0 };
  let critical = 0;
  let warning = 0;

  live.forEach((order) => {
    const delay = order.delay || getDelayAttribution(order);
    if (totals[delay.source] != null && delay.isDelayed) totals[delay.source] += 1;
    if (delay.severity === 'critical') critical += 1;
    if (delay.severity === 'warning') warning += 1;
  });

  const delayedTotal = Object.values(totals).reduce((sum, value) => sum + value, 0);
  const dominantSource = Object.entries(totals).sort((a, b) => b[1] - a[1])[0]?.[0] || 'none';

  return {
    liveCount: live.length,
    delayedTotal,
    critical,
    warning,
    dominantSource,
    totals,
    percentages: Object.fromEntries(
      Object.entries(totals).map(([key, value]) => [key, delayedTotal ? Math.round((value / delayedTotal) * 100) : 0])
    ),
  };
}

export function storePressureScore(orders = []) {
  const live = orders.filter((order) => !['delivered', 'cancelled'].includes(order.stage));
  const critical = live.filter((order) => (order.delay || getDelayAttribution(order)).severity === 'critical').length;
  const warning = live.filter((order) => (order.delay || getDelayAttribution(order)).severity === 'warning').length;
  const score = live.length * 8 + warning * 18 + critical * 30;

  if (score >= 90) return { label: 'Critical', severity: 'critical', score };
  if (score >= 55) return { label: 'Under pressure', severity: 'warning', score };
  if (score >= 25) return { label: 'Busy', severity: 'busy', score };
  return { label: 'Healthy', severity: 'healthy', score };
}
